<template>
  <div class="px-2 py-4 sm:px-4 sm:py-6">
    <!-- Sync Progress Modal -->
    <SyncProgressModal
      :is-open="isModalOpen"
      sync-type="employees-all"
      :progress="progress"
      :has-error="hasError"
      :error-message="errorMessage"
      @close="closeModal"
      @retry="retrySync"
    />

    <!-- Sync Results -->
    <SyncResultNotification :result="syncResult" @close="syncResult = null" />

    <!-- Filters -->
    <EmployeeFilter
      v-model="filters"
      :functions="uniqueFunctions"
      :aanspreekpunten="aanspreekpunten || []"
      show-employee-counts
      hide-availability-mode
    >
      <template #actions>
        <div class="relative">
          <span
            v-if="lastSyncedLabel"
            :title="lastSyncedFull"
            class="absolute bottom-full right-0 mb-0.5 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap"
          >
            {{ lastSyncedLabel }}
          </span>
          <button
            @click="startSync"
            :disabled="isSyncing"
            class="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap text-sm"
          >
            <ArrowPathIcon class="h-4 w-4" :class="{ 'animate-spin': isSyncing }" />
            <span class="hidden sm:inline">Volledige sync</span>
          </button>
        </div>
      </template>
    </EmployeeFilter>

    <!-- Loading state -->
    <div v-if="isLoading" class="text-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p class="mt-4 text-gray-600 dark:text-gray-400">Laden...</p>
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
    >
      <p class="text-red-800 dark:text-red-300">
        Fout bij laden van medewerkers: {{ error.message }}
      </p>
    </div>

    <!-- Employee grid -->
    <div v-else class="grid gap-3 sm:gap-4 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
      <EmployeeCard
        v-for="employee in filteredEmployees"
        :key="employee.id"
        :employee="employee"
        @view-details="openEmployeeModal"
      />
    </div>

    <!-- Empty state -->
    <div v-if="!isLoading && !error && filteredEmployees.length === 0" class="text-center py-12">
      <UsersIcon class="mx-auto h-12 w-12 text-gray-400" />
      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">
        Geen medewerkers gevonden
      </h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Pas je filters aan of voeg nieuwe medewerkers toe.
      </p>
    </div>

    <!-- Employee detail modal -->
    <EmployeeModal
      v-if="selectedEmployee"
      :employee="selectedEmployee"
      :period-start="dateFilters.start_date"
      :period-end="dateFilters.end_date"
      @close="selectedEmployee = null"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { ArrowPathIcon, UsersIcon } from '@heroicons/vue/24/outline'
import { employeeService } from '@/services/employeeService.js'
import { useSyncWithProgress } from '@/composables/data/useSyncWithProgress'
import { useLastSynced } from '@/composables/data/useLastSynced'
import EmployeeFilter from '@/components/common/inputs/EmployeeFilter.vue'
import EmployeeCard from '@/components/common/cards/EmployeeCard.vue'
import EmployeeModal from '@/components/employee/EmployeeModal.vue'
import SyncResultNotification from '@/components/common/feedback/SyncResultNotification.vue'
import SyncProgressModal from '@/components/common/modals/SyncProgressModal.vue'
import { SWP_EMPLOYEE_ID } from '@/constants/swp.js'

const toLocalDateStr = date => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Set default date range: Monday of the current week to 8 weeks from now
const now = new Date()
const currentMonday = new Date(now)
const day = currentMonday.getDay()
const diffToMonday = day === 0 ? -6 : 1 - day
currentMonday.setDate(currentMonday.getDate() + diffToMonday)
const monday = toLocalDateStr(currentMonday)
const eightWeeksFromNow = new Date(now)
eightWeeksFromNow.setDate(eightWeeksFromNow.getDate() + 56)
const eightWeeksFromNowStr = toLocalDateStr(eightWeeksFromNow)

// Separate date filters from other filters
const dateFilters = ref({
  start_date: monday,
  end_date: eightWeeksFromNowStr
})

const clientFilters = ref({
  team_ids: [],
  functions: [],
  employment_status: '',
  search: '',
  aanspreekpunt_ids: []
})

// Combined filters for the EmployeeFilter component
const filters = computed({
  get: () => ({
    ...clientFilters.value,
    ...dateFilters.value
  }),
  set: newFilters => {
    // Separate date filters from client filters
    const { start_date, end_date, ...otherFilters } = newFilters

    // Update date filters if they changed
    if (start_date !== dateFilters.value.start_date || end_date !== dateFilters.value.end_date) {
      dateFilters.value = { start_date, end_date }
    }

    // Update client filters
    clientFilters.value = otherFilters
  }
})

const selectedEmployee = ref(null)
const queryClient = useQueryClient()

// Sync with progress tracking
const {
  isModalOpen,
  isSyncing,
  progress,
  hasError,
  errorMessage,
  syncResult,
  startSync,
  retrySync,
  closeModal
} = useSyncWithProgress('employees-all', employeeService.syncAll, {
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['employees'] })
    queryClient.invalidateQueries({ queryKey: ['teams'] })
  }
})

const { lastSyncedLabel, lastSyncedFull } = useLastSynced(['employees', 'employees-all'])

// Fetch employees with only date filters in query key
const {
  data: employees,
  isLoading,
  error
} = useQuery({
  queryKey: ['employees', dateFilters],
  queryFn: async () => {
    const queryParams = {
      start_date: dateFilters.value.start_date,
      end_date: dateFilters.value.end_date,
      include_availability: 'true',
      is_sourceable: 'true'
    }

    return await employeeService.getAll(queryParams)
  },
  staleTime: 1000 * 60 * 2 // 2 minutes (shorter since availability changes frequently)
})

// Fetch aanspreekpunten for filter dropdown
const { data: aanspreekpunten } = useQuery({
  queryKey: ['aanspreekpunten'],
  queryFn: employeeService.getAanspreekpunten,
  staleTime: 1000 * 60 * 10 // 10 minutes
})

// Fetch filter options independently (so they don't change when filtering)
const { data: filterOptions } = useQuery({
  queryKey: ['filter-options'],
  queryFn: employeeService.getFilterOptions,
  staleTime: 1000 * 60 * 10 // 10 minutes
})

// Use filter options from the API (stable, doesn't change with filtering)
const uniqueFunctions = computed(() => filterOptions.value?.functions || [])

const filteredEmployees = computed(() => {
  if (!employees.value) return []

  return employees.value.filter(employee => {
    // Hide SWP placeholder from employee overview
    if (employee.simplicate_id === SWP_EMPLOYEE_ID) return false

    // Apply all client-side filters with OR logic for arrays
    const teamIds = clientFilters.value.team_ids || []
    const matchesTeam =
      teamIds.length === 0 || employee.teams?.some(team => teamIds.includes(team.simplicate_id))

    const functions = clientFilters.value.functions || []
    const matchesFunction = functions.length === 0 || functions.includes(employee.function)

    const matchesStatus =
      !clientFilters.value.employment_status ||
      employee.employment_status === clientFilters.value.employment_status

    const matchesSearch =
      !clientFilters.value.search ||
      employee.name.toLowerCase().includes(clientFilters.value.search.toLowerCase())

    const aanspreekpuntIds = clientFilters.value.aanspreekpunt_ids || []
    const matchesAanspreekpunt =
      aanspreekpuntIds.length === 0 || aanspreekpuntIds.includes(employee.aanspreekpunt_id)

    return matchesTeam && matchesFunction && matchesStatus && matchesSearch && matchesAanspreekpunt
  })
})

const openEmployeeModal = employee => {
  selectedEmployee.value = employee
}
</script>
